/**
 * Depth progress bar: distance into the current 50 m checkpoint, and what rock is
 * coming next.
 *
 * The material half is a *prediction*, not a readout. The server rolls the material
 * per rock with jitter either way (`run_engine.roll_material`), so this names the
 * typical rock at that depth and the copy says "typically" for that reason. It is
 * derived from the generated `MATERIALS` table via `game/engine/materials.ts` —
 * never hand-listed here, because a HUD naming rock the server cannot spawn is how
 * this component read for its first two revisions.
 */
import { Icon } from '@/components/ui/Icon';
import { nextMaterialAfter } from '@/game/engine/materials';

interface DepthTrackerProps {
  depth: number;
  maxDepth?: number | null | undefined;
}

const CHECKPOINT_M = 50;

export function DepthTracker({ depth, maxDepth }: DepthTrackerProps) {
  const roundedDepth = Math.max(0, Math.floor(depth));

  // Next 50m checkpoint
  const nextCheckpoint = (Math.floor(roundedDepth / CHECKPOINT_M) + 1) * CHECKPOINT_M;
  const currentCheckpointBase = Math.floor(roundedDepth / CHECKPOINT_M) * CHECKPOINT_M;
  const progressPct = Math.min(
    100,
    Math.max(0, ((roundedDepth - currentCheckpointBase) / CHECKPOINT_M) * 100)
  );

  /* null past the curve's saturation depth — the endless stretch, where the rock
     stops getting harder but the cave keeps going. */
  const nextMaterial = nextMaterialAfter(roundedDepth);

  return (
    /* Was bg-slate-950 — a cool blue-grey band across the top of a warm cave,
       and the only cold surface on the screen. It read as a different app's
       chrome bolted above the game. The cave's own surface token instead. */
    <div className="shrink-0 border-b border-[var(--hairline)] bg-[var(--surface)]/85
                    px-3 py-1 backdrop-blur-md select-none">
      <div className="flex items-center justify-between gap-2 text-[9px] font-medium">
        {/* Checkpoint Progress Indicator */}
        <div className="flex items-center gap-1.5">
          <span className="font-pixel text-[8px] text-amber-300 flex items-center gap-1">
            <Icon name="target" size={10} className="text-amber-400" />
            <span>{roundedDepth}m</span>
          </span>
          <span className="text-[8px] text-ink-dim font-pixel flex items-center gap-1">
            <span>/</span>
            <Icon name="flag" size={10} className="text-amber-300/80" />
            <span>{nextCheckpoint}m</span>
          </span>
        </div>

        {/* Next Rock Tier Distance */}
        {nextMaterial ? (
          <div className="flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 border border-amber-500/25 font-pixel text-[7px] text-amber-200">
            <Icon name="rock" size={10} className="text-amber-400" />
            <span>{nextMaterial.material.name}</span>
            <span className="text-amber-400">(typically {nextMaterial.metersAway}m away)</span>
          </div>
        ) : (
          <span className="rounded bg-violet-500/20 px-1.5 py-0.5 font-pixel text-[7px] text-violet-300 border border-violet-500/30 flex items-center gap-1">
            <Icon name="sparkle" size={10} className="text-violet-300" />
            <span>ENDLESS ABYSS</span>
          </span>
        )}

        {/* Lamp Ceiling Limit if finite */}
        {maxDepth != null && (
          <span className="font-pixel text-[7px] text-orange-300/80">
            LIMIT {maxDepth}m
          </span>
        )}
      </div>

      {/* Mini Progress Track */}
      <div className="mt-1 flex items-center gap-1.5">
        <div className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
          <i
            className="block h-full rounded-full bg-gradient-to-r from-amber-500 via-orange-400 to-amber-300 transition-[width] duration-300 shadow-[0_0_8px_rgba(245,158,11,0.6)]"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <span className="font-pixel text-[7.5px] text-amber-400 tabular">
          {Math.round(progressPct)}%
        </span>
      </div>
    </div>
  );
}
